/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { DialogDefaultV2, EmptyState } from '../../../components/v2';
import { chipColor, getRegra } from './constants';
import ResumoHoras from './ResumoHoras';

moment.locale('pt-br');

const DiaProblema = ({ dia, onOpenJornada }) => (
  <Box sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
        {dia.data.format('DD/MM/YYYY — dddd')}
      </Typography>
      {dia.jornadaId && (
        <Button
          size="small"
          color="inherit"
          startIcon={<OpenInNewOutlinedIcon />}
          onClick={() => onOpenJornada(dia.jornadaId)}
          sx={{ flexShrink: 0, mt: -0.5 }}
        >
          Abrir jornada
        </Button>
      )}
    </Stack>
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
      {dia.regras.map((regraId) => {
        const regra = getRegra(regraId);
        if (!regra) return null;
        return (
          <Chip
            key={regraId}
            size="small"
            label={`${regra.short} · ${regra.label}`}
            color={chipColor(regraId)}
            variant={regraId === 'SEM_JORNADA' ? 'outlined' : 'filled'}
          />
        );
      })}
    </Stack>
    <Stack spacing={0.25} sx={{ mt: 0.75 }}>
      {dia.detalhes.length > 0 ? dia.detalhes.map((texto) => (
        <Typography key={texto} variant="body2" color="text.secondary">{texto}</Typography>
      )) : (
        <Typography variant="body2" color="text.secondary">
          {dia.regras.map((id) => getRegra(id)?.label).filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Stack>
  </Box>
);

DiaProblema.propTypes = {
  dia: PropTypes.shape({
    data: PropTypes.object.isRequired,
    regras: PropTypes.arrayOf(PropTypes.string).isRequired,
    detalhes: PropTypes.arrayOf(PropTypes.string).isRequired,
    jornadaId: PropTypes.string,
  }).isRequired,
  onOpenJornada: PropTypes.func.isRequired,
};

const DiasViolacaoDialog = ({
  open,
  nome,
  row,
  dias,
  onClose,
  onOpenJornada,
  onOpenConformidade,
  canOpenConformidade,
}) => {
  const total = dias.length;
  const showConformidade = Boolean(canOpenConformidade && onOpenConformidade && row?.inspetorId);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={nome || 'Dias com violação'}
      readOnly
      maxWidth="sm"
      fullWidth
      headerAction={showConformidade ? {
        title: 'Ver conformidade',
        variant: 'outlined',
        onClick: () => onOpenConformidade(row),
      } : null}
    >
      <Stack spacing={2}>
        <ResumoHoras row={row} />
        {showConformidade && (
          <Typography variant="body2" color="text.secondary">
            Para ver se trabalhou de fato (programação, convocação, campo), abra a conformidade deste inspetor.
          </Typography>
        )}
        {total === 0 ? (
          <EmptyState
            title="Nenhum dia detalhado"
            message="O relatório não trouxe os dias deste inspetor. A contagem da tabela veio do ranking, sem a lista do período."
          />
        ) : (
          <Stack spacing={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {total === 1 ? '1 dia com problema' : `${total} dias com problema`}
              {' · do mais antigo ao mais recente'}
            </Typography>
            {dias.map((dia) => (
              <DiaProblema key={dia.id} dia={dia} onOpenJornada={onOpenJornada} />
            ))}
          </Stack>
        )}
      </Stack>
    </DialogDefaultV2>
  );
};

DiasViolacaoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  nome: PropTypes.string,
  row: PropTypes.object,
  dias: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenJornada: PropTypes.func.isRequired,
  onOpenConformidade: PropTypes.func,
  canOpenConformidade: PropTypes.bool,
};

export default DiasViolacaoDialog;
